/**
 * =====================================================================
 * apps/web/src/lib/stripe/telemetry.ts
 * =====================================================================
 * Telemetría unificada de ERRORES DE PAGO.
 *
 * En cada punto de abandono del funnel de pago (carga de stripe.js,
 * loaderror del elemento, excepción/timeout de confirmación, error de
 * verifyIdentity, timeouts de polling, invocar edge functions) se llama
 * a `reportPaymentError` con el `step` concreto.
 *
 * Hace DOS cosas (y nunca rompe el flujo de pago):
 *   1. Sentry: captureException con nivel `error` y `tags: { area:
 *      "stripe-payment", step }` + `extra.user_agent`. Así el abandono
 *      es diagnosticable por dispositivo/navegador (incidente Android
 *      10 / Samsung Internet).
 *   2. GA4/GTM: evento `bs_payment_error` (dataLayer) para medir el
 *      abandono por paso en informes.
 * =====================================================================
 */
import * as Sentry from "@sentry/react";
import { trackEvent } from "@/lib/dataLayer";

/** Identificador corto del paso del funnel donde falló el pago. */
export type PaymentErrorStep =
  | "load_stripe_js"
  | "create_setup_invoke"
  | "element_load_error"
  | "element_timeout"
  | "confirm_exception"
  | "confirm_timeout"
  | "setup_polling_timeout"
  | "membership_invoke"
  | "membership_polling_timeout"
  | "identity_verify_error"
  | "identity_verify_timeout"
  | "brickpoints_invoke"
  | "borrowing_invoke";

const AREA_TAG = "stripe-payment";

function safeUserAgent(): string | undefined {
  try {
    return typeof navigator !== "undefined" ? navigator.userAgent : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reporta un error de pago a Sentry + GA4. Nunca lanza.
 *
 * @param step  Paso del funnel (ver PaymentErrorStep).
 * @param error El error original (Error, objeto de Stripe, string…).
 * @param extra Campos adicionales de contexto (p. ej. `code` de Stripe).
 */
export function reportPaymentError(
  step: PaymentErrorStep,
  error: unknown,
  extra?: Record<string, unknown>
): void {
  const reason =
    error instanceof Error
      ? error
      : typeof error === "string"
        ? new Error(error)
        : error && typeof error === "object"
          ? new Error(
              (error as { message?: string }).message ||
                (error as { code?: string }).code ||
                JSON.stringify(error)
            )
          : new Error(String(error ?? "unknown payment error"));

  // 1. Sentry — observabilidad por dispositivo/navegador.
  try {
    Sentry.captureException(reason, {
      level: "error",
      tags: { area: AREA_TAG, step },
      extra: {
        user_agent: safeUserAgent(),
        ...extra,
      },
    });
  } catch {
    /* SDK no inicializado (dev/tests) */
  }

  // 2. GA4/GTM — medición de abandono por paso.
  try {
    trackEvent("bs_payment_error", {
      step,
      error: extra?.code ?? reason.message.slice(0, 120),
    });
  } catch {
    /* dataLayer no disponible */
  }
}
